import { useEffect, useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import LazyImage from "../../common/LazyImage";
import { getProjects, getCategories } from "../../api/api";
import { useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/logo.png";

export default function Projects({ isHome = false, onLoadComplete = null }) {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [catId, setCatId] = useState("All");
  const [open, setOpen] = useState(null);
  const [slide, setSlide] = useState(0);
  const scrollY = useRef(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isProjectPage = location.pathname.includes("/projects");
  const sectionRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projData, catData] = await Promise.all([
          getProjects(),
          getCategories()
        ]);
        setProjects(projData);
        setCategories(catData);
      } catch (err) {
        console.error("Error fetching project data:", err);
      } finally {
        setIsLoading(false);
        if (onLoadComplete) onLoadComplete();
      }
    };
    fetchData();
  }, []);

  const filtered = useMemo(() => {
    if (isHome) return projects.slice(0, 5);
    if (catId === "All") return projects;
    return projects.filter((p) => String(p.categoryId) === String(catId));
  }, [catId, projects, isHome]);

  const visibleCount = isProjectPage ? filtered.length : (isHome ? 5 : 12);

  useEffect(() => {
    if (isProjectPage) {
      const timeout = setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 800);
      return () => clearTimeout(timeout);
    }
  }, [isProjectPage]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(null);
      if (e.key === "ArrowRight") setSlide((s) => (s + 1) % open.images.length);
      if (e.key === "ArrowLeft")
        setSlide((s) => (s - 1 + open.images.length) % open.images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    setImageLoaded(false);
  }, [slide, open]);

  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;

    if (open) {
      scrollY.current = window.scrollY;
      body.style.position = "fixed";
      body.style.top = `-${scrollY.current}px`;
      body.style.width = "100%";
      body.style.overflowY = "hidden";
      html.style.overflow = "hidden";
    } else {
      const y = scrollY.current;
      body.style.position = "";
      body.style.top = "";
      body.style.width = "";
      body.style.overflowY = "";
      html.style.overflow = "";

      window.scrollTo({ top: y });
    }

    return () => {
      body.style.position = "";
      body.style.top = "";
      body.style.width = "";
      body.style.overflowY = "";
      html.style.overflow = "";
    };
  }, [open]);

  if (isLoading && !onLoadComplete) {
    return (
      <section className="container bg-transparent py-20 ">
        <div className="w-full">
          <div className="h-10 w-64 bg-white/5 rounded-md animate-pulse mb-10" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="aspect-[3/4] bg-white/5 rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="projects"
      ref={sectionRef}
      className="container relative py-10 bg-transparent scroll-mt-24 overflow-hidden"
    >
      <div className="w-full">
        <p className="text-[11px] md:text-xs tracking-[0.28em] uppercase text-white/70">
          Latest Projects
        </p>
        <h2 className="mt-3 text-3xl md:text-5xl font-extrabold text-white leading-tight">
          Featured <span className="text-secondary italic">Architecture.</span>
        </h2>
        <p className="mt-4 text-white/80 md:text-lg leading-relaxed max-w-3xl">
          Explore our core categories. Click a tile to view subcategories and
          product details on the Products page.
        </p>
        {(isHome || !isProjectPage) && projects.length > 6 && (
          <div className="text-start mt-8">
            <button
              onClick={() => navigate("/projects")}
              className="inline-flex items-center gap-3 justify-center px-5 py-3 rounded-md border border-white/20 text-white hover:bg-white/10 mb-8"

            > 
              + View All Projects
            </button>
          </div>
        )}
        {!isHome && (
          <div className="flex flex-wrap gap-3 mb-8">
            <button
              onClick={() => setCatId("All")}
              className={`px-4 py-2 rounded-md text-sm border transition-all ${catId === "All"
                ? "bg-white text-black border-white"
                : "border-white/30 text-white/80 hover:bg-white/10"
                }`}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c.categoryId}
                onClick={() => setCatId(c.categoryId)}
                className={`px-4 py-2 rounded-md text-sm border transition-all ${String(catId) === String(c.categoryId)
                  ? "bg-white text-black border-white"
                  : "border-white/30 text-white/80 hover:bg-white/10"
                  }`}
              >
                {c.categoryName}
              </button>
            ))}
          </div>
        )}

        {filtered.length > 0 ? (
          <div className={`mt-8 gap-4 space-y-4 ${isHome
            ? "columns-2 md:columns-3 lg:columns-3"
            : "columns-2 md:columns-3 lg:columns-4"
            }`}>
            {filtered.slice(0, visibleCount).map((p, index) => (
              <motion.div
                key={p.projectId}
                layout
                onClick={() => {
                  setOpen(p);
                  setSlide(0);
                }}
                className="relative break-inside-avoid overflow-hidden border border-white/10 cursor-pointer group rounded-lg"
              >
                <div className="relative aspect-auto">
                  {p.images && p.images.length > 0 ? (
                    <LazyImage
                      src={p.images[0]}
                      alt={p.projectName}
                      priority={index < 8}
                      className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full aspect-square bg-neutral-900 flex items-center justify-center p-8 opacity-20">
                      <img src={logo} alt="" className="w-full" />
                    </div>
                  )}
                </div>
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-all duration-500 flex flex-col items-center justify-center text-center p-4">
                  <p className="text-white font-bold">{p.projectName}</p>
                  <p className="text-white/60 text-xs mt-1 uppercase tracking-widest">{p.categoryName}</p>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="mt-8 py-32 text-center border border-dashed border-white/10 bg-white/[0.02] rounded-[40px] backdrop-blur-sm">
            <div className="w-20 h-20 bg-white/[0.03] rounded-full flex items-center justify-center mx-auto mb-6 border border-white/5 animate-pulse">
              <i className="ri-building-2-line text-4xl text-white/20"></i>
            </div>
            <p className="text-white/40 font-black uppercase tracking-[0.4em] text-[11px]">Showcase projects coming soon</p>
            <div className="w-12 h-1 bg-white/5 mx-auto mt-6" />
          </div>
        )}
      </div>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 z-[100] bg-black/95"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(null)}
            />

            <motion.div
              className="fixed inset-0 z-[101] flex items-center justify-center p-4"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              <div className="relative w-full max-w-6xl h-[80vh] flex flex-col">
                <div className="flex-1 relative overflow-hidden bg-black rounded-xl flex items-center justify-center">
                  {!imageLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center z-50 bg-black">
                      <div className="flex flex-col items-center gap-4">
                        <motion.i 
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                          className="ri-loader-line text-4xl text-white/30"
                        />
                        <span className="text-[10px] uppercase tracking-[0.3em] text-white/20 font-medium">Fetching View</span>
                      </div>
                    </div>
                  )}

                  <AnimatePresence mode="wait">
                    <motion.img
                      key={open.images[slide]}
                      src={open.images[slide]}
                      alt={open.projectName}
                      className="absolute inset-0 w-full h-full object-contain"
                      initial={{ opacity: 0 }}
                      animate={imageLoaded ? { opacity: 1 } : { opacity: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      onLoad={() => setImageLoaded(true)}
                    />
                  </AnimatePresence>

                  <button
                    onClick={() => setOpen(null)}
                    className="absolute top-4 right-4 z-[110] bg-black/40 hover:bg-white/20 text-white rounded-full h-10 w-10 flex items-center justify-center transition-all"
                  >
                    ✕
                  </button>

                  {open.images.length > 1 && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSlide((s) => (s - 1 + open.images.length) % open.images.length);
                        }}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full h-12 w-12 flex items-center justify-center"
                      >
                        ‹
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSlide((s) => (s + 1) % open.images.length);
                        }}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full h-12 w-12 flex items-center justify-center"
                      >
                        ›
                      </button>
                    </>
                  )}
                </div>

                <div className="mt-4 p-4 text-center">
                  <h3 className="text-xl font-bold text-white">{open.projectName}</h3>
                  <p className="text-white/60 text-sm mt-1">{open.categoryName}</p>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </section>
  );
}
